import { useStopImpersonation } from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { Button } from '@hostesa/ui';

/**
 * PERSISTENT impersonation banner for the platform (hotel) app.
 *
 * When an operator is acting on behalf of a hotel user, GET /me reports
 * `impersonation: { active, byUserId, label }`. This banner is mounted at the
 * app root so it is ALWAYS visible (regardless of route) while the session is an
 * impersonation session, so the operator can never forget who they are acting
 * as.
 *
 * SECURITY / PII:
 * - Driven purely by `useAuth().impersonation` (server-reported display state).
 *   The banner never grants permissions; the backend enforces every action.
 * - `label` is a non-PII display string (e.g. "Hotel Admin — Vila Rila").
 * - Exit best-effort calls `POST /operator/impersonate/stop` (ignoring errors),
 *   then logs out — which clears the in-memory session token and routes to
 *   /login. Even if the stop call fails, logout ends the impersonated session
 *   on this client.
 */
export function ImpersonationBanner() {
  const { t } = useT('platform');
  const { impersonation, logout } = useAuth();
  const stopImpersonation = useStopImpersonation();

  if (!impersonation?.active) return null;

  const handleExit = async () => {
    try {
      await stopImpersonation.mutateAsync();
    } catch {
      // Best-effort: even if the server-side stop fails, log out locally so the
      // impersonation token is cleared from this client.
    }
    logout();
  };

  return (
    <div
      data-testid="impersonation-banner"
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-4 border-b border-[var(--color-alert)] bg-[var(--color-alert)]/10 px-6 py-2 text-sm text-[var(--color-charcoal)]"
    >
      <span data-testid="impersonation-banner-label" className="flex flex-wrap items-center gap-2">
        <strong className="font-[var(--font-display)] text-[var(--color-alert)]">
          {t('impersonation.bannerLabel')}
        </strong>
        <span>
          {t('impersonation.actingAs')} <strong>{impersonation.label}</strong>
        </span>
      </span>
      <Button
        data-testid="impersonation-exit-btn"
        variant="secondary"
        size="sm"
        disabled={stopImpersonation.isPending}
        onClick={() => void handleExit()}
      >
        {t('impersonation.exit')}
      </Button>
    </div>
  );
}
